import React, { useState, useRef, useEffect } from 'react';

const AddMissingChildModal = ({
  isOpen,
  onClose,
  preliminaryProposal,
  parentNode,
  existingChildren,
  onAddChild,
  wsRef
}) => {
  const [step, setStep] = useState(1); // 1: parent selection, 2: child selection, 3: position
  const [selectedParentText, setSelectedParentText] = useState('');
  const [selectedChildText, setSelectedChildText] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const parentTextRef = useRef(null);
  const childTextRef = useRef(null);
  
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedParentText('');
      setSelectedChildText('');
      setSelectedPosition(0);
      setIsProcessing(false);
    }
  }, [isOpen]);
  
  const handleTextSelection = (ref, setter) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString().trim();
      if (selectedText) {
        setter(selectedText);
      }
    }
  };
  
  const handleParentTextMouseUp = () => {
    setTimeout(() => handleTextSelection(parentTextRef, setSelectedParentText), 10);
  };
  
  const handleChildTextMouseUp = () => {
    setTimeout(() => handleTextSelection(childTextRef, setSelectedChildText), 10);
  };
  
  const handleNext = () => {
    if (step === 1) {
      if (!selectedParentText.trim()) {
        alert('Please select the parent section from the preliminary proposal');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!selectedChildText.trim()) {
        alert('Please select the missing child text from the preliminary proposal');
        return;
      }
      setStep(3);
    }
  };
  
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedParentText.trim() || !selectedChildText.trim()) {
      alert('Please complete all steps');
      return;
    }
    
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('WebSocket connection not available');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const message = {
        command: 'add_missing_child',
        plan_id: parentNode?.planId || 'default_proposal_plan',
        parent_node_id: parentNode?.id,
        selected_parent_text: selectedParentText,
        selected_child_text: selectedChildText,
        position_hint: selectedPosition,
        preliminary_proposal: preliminaryProposal,
        user_command: ''
      };
      
      wsRef.current.send(JSON.stringify(message));
      
      // Response will be handled by parent component's WebSocket handler
      // Just show processing state - parent will call onAddChild when done
      
    } catch (error) {
      setIsProcessing(false);
      alert(`Error: ${error.message}`);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 16,
      boxSizing: 'border-box'
    }}>
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: 12,
        padding: 20,
        width: '100%',
        maxWidth: 820,
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 12,
          flexShrink: 0,
          gap: 12
        }}>
          <h2 style={{
            color: '#e2e8f0',
            fontSize: 16,
            fontWeight: 600,
            margin: 0,
            lineHeight: 1.35,
            wordBreak: 'break-word',
            minWidth: 0,
            flex: 1
          }}>
            Add Missing Child to "{parentNode?.title || 'Parent'}"
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: 24,
              cursor: 'pointer',
              padding: 0,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Step indicator */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexShrink: 0
        }}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                flex: 1,
                height: 4,
                backgroundColor: s <= step ? '#5865f2' : '#334155',
                borderRadius: 2
              }}
            />
          ))}
        </div>

        {/* Scrollable step content */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          marginBottom: 16
        }}>
        {/* Step 1: Parent Selection */}
        {step === 1 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: 8, fontSize: 14 }}>
              Step 1: Select Parent Section
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: 12, fontSize: 13 }}>
              Select the parent section from the preliminary proposal that corresponds to "{parentNode?.title}"
            </p>
            <div
              ref={parentTextRef}
              onMouseUp={handleParentTextMouseUp}
              style={{
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                padding: 12,
                minHeight: 140,
                maxHeight: 240,
                overflow: 'auto',
                color: '#cbd5e1',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                cursor: 'text',
                userSelect: 'text'
              }}
            >
              {preliminaryProposal || 'No preliminary proposal available'}
            </div>
            {selectedParentText && (
              <div style={{
                marginTop: 10,
                padding: 10,
                backgroundColor: '#1e3a5f',
                borderRadius: 6,
                border: '1px solid #3b82f6'
              }}>
                <div style={{ color: '#93c5fd', fontSize: 11, marginBottom: 4 }}>Selected:</div>
                <div style={{ color: '#e2e8f0', fontSize: 12 }}>{selectedParentText.substring(0, 200)}...</div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Child Selection */}
        {step === 2 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: 8, fontSize: 14 }}>
              Step 2: Select Missing Child Text
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: 12, fontSize: 13 }}>
              Select the missing child text from the preliminary proposal
            </p>
            <div
              ref={childTextRef}
              onMouseUp={handleChildTextMouseUp}
              style={{
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                padding: 12,
                minHeight: 140,
                maxHeight: 240,
                overflow: 'auto',
                color: '#cbd5e1',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                cursor: 'text',
                userSelect: 'text'
              }}
            >
              {preliminaryProposal || 'No preliminary proposal available'}
            </div>
            {selectedChildText && (
              <div style={{
                marginTop: 10,
                padding: 10,
                backgroundColor: '#1e3a5f',
                borderRadius: 6,
                border: '1px solid #3b82f6'
              }}>
                <div style={{ color: '#93c5fd', fontSize: 11, marginBottom: 4 }}>Selected:</div>
                <div style={{ color: '#e2e8f0', fontSize: 12 }}>{selectedChildText.substring(0, 200)}...</div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Position Selection */}
        {step === 3 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: 8, fontSize: 14 }}>
              Step 3: Choose Position
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: 12, fontSize: 13 }}>
              Drag the new child to position it between existing children
            </p>
            <div style={{
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: 8,
              padding: 12,
              minHeight: 140,
              maxHeight: 240,
              overflow: 'auto'
            }}>
              {/* Add at beginning */}
              <div
                onClick={() => setSelectedPosition(0)}
                style={{
                  padding: 12,
                  marginBottom: 8,
                  borderRadius: 6,
                  backgroundColor: selectedPosition === 0 ? '#1e3a5f' : '#1e293b',
                  border: selectedPosition === 0 ? '1px solid #3b82f6' : '1px solid #334155',
                  cursor: 'pointer',
                  color: '#e2e8f0'
                }}
              >
                ➕ Add at the beginning
              </div>
              
              {/* Add after each existing child */}
              {existingChildren.map((child, index) => (
                <div key={child.id || index}>
                  <div style={{
                    padding: 12,
                    marginBottom: 8,
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: 6,
                    color: '#cbd5e1',
                    fontSize: 13
                  }}>
                    {index + 1}. {child.title || 'Untitled'}
                  </div>
                  <div
                    onClick={() => setSelectedPosition(index + 1)}
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      borderRadius: 6,
                      backgroundColor: selectedPosition === index + 1 ? '#1e3a5f' : '#1e293b',
                      border: selectedPosition === index + 1 ? '1px solid #3b82f6' : '1px solid #334155',
                      cursor: 'pointer',
                      color: '#e2e8f0',
                      fontSize: 13
                    }}
                  >
                    ➕ Add after this child
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>

        {/* Buttons - always visible at bottom */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexShrink: 0,
          paddingTop: 4
        }}>
          <div>
            {step > 1 && (
              <button
                onClick={handleBack}
                disabled={isProcessing}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#334155',
                  border: '1px solid #475569',
                  borderRadius: 6,
                  color: '#e2e8f0',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              disabled={isProcessing}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                border: '1px solid #475569',
                borderRadius: 6,
                color: '#94a3b8',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            {step < 3 ? (
              <button
                onClick={handleNext}
                disabled={isProcessing}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#5865f2',
                  border: 'none',
                  borderRadius: 6,
                  color: '#ffffff',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isProcessing}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isProcessing ? '#475569' : '#5865f2',
                  border: 'none',
                  borderRadius: 6,
                  color: '#ffffff',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                {isProcessing ? 'Processing...' : 'Add Child'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMissingChildModal;
